import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

const STORAGE_KEY = "andreas_session";

/** Starter suggestion chips shown when the conversation is empty. */
const STARTERS = {
  en: ["What time is the ceremony?", "Dress code?", "How do I get there?", "Can I bring my kids?"],
  fr: ["À quelle heure la cérémonie ?", "Quelle tenue ?", "Comment on y va ?", "Puis-je venir avec mes enfants ?"],
  el: ["Τι ώρα η τελετή;", "Ενδυματολογικός κώδικας;", "Πώς πάω εκεί;", "Μπορώ να φέρω τα παιδιά;"],
  pl: ["O której ceremonia?", "Jak się ubrać?", "Jak tam dotrzeć?", "Czy mogę zabrać dzieci?"],
};

const HEADINGS = {
  en: { title: "Andreas", subtitle: "Ask me anything about the wedding · or Cyprus", placeholder: "Type your question…" },
  fr: { title: "Andreas", subtitle: "Demandez-moi tout sur le mariage · ou Chypre", placeholder: "Votre question…" },
  el: { title: "Andreas", subtitle: "Ρωτήστε με για τον γάμο · ή την Κύπρο", placeholder: "Η ερώτησή σας…" },
  pl: { title: "Andreas", subtitle: "Zapytaj o ślub · lub o Cypr", placeholder: "Twoje pytanie…" },
};

const AndreasChat = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const scrollRef = useRef(null);

  const h = HEADINGS[lang] || HEADINGS.en;
  const starters = STARTERS[lang] || STARTERS.en;

  // Scroll to bottom whenever the message list changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const send = useCallback(async (raw) => {
    const text = (raw || input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chatbot/ask", {
        message: text,
        session_id: sessionId,
        language: lang,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch { /* ignore */ }
      }
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Andreas is having a nap. Try again.";
      setMessages((m) => [...m, { role: "assistant", text: detail, error: true }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, sessionId, lang]);

  const onSubmit = (e) => { e.preventDefault(); send(); };

  return (
    <>
      {/* Floating trigger button with attention-grabbing teaser */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-50 flex items-end gap-2" data-testid="andreas-chat-trigger-wrap">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="andreas-chat-teaser"
            className="relative flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-3.5 sm:py-2.5 rounded-xl sm:rounded-2xl bg-foreground text-background shadow-xl max-w-[52vw] sm:max-w-none animate-bounce-slow hover:brightness-110 transition-all"
            aria-label="Open Andreas chat"
          >
            <span className="relative inline-flex w-1.5 h-1.5 sm:w-2 sm:h-2 shrink-0">
              <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-70" />
              <span className="relative inline-flex w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-accent" />
            </span>
            {/* Short teaser on mobile */}
            <span className="sm:hidden text-[10px] tracking-[0.1em] uppercase font-medium whitespace-nowrap">
              {lang === "pl" ? "Zapytaj Andreasa"
                : lang === "el" ? "Ρώτα τον Andreas"
                : lang === "fr" ? "Demande à Andreas"
                : "Ask Andreas"}
            </span>
            {/* Full teaser on ≥sm */}
            <span className="hidden sm:inline text-[12px] tracking-[0.12em] uppercase font-medium whitespace-nowrap">
              {lang === "pl" ? "Zapytaj Andreasa, reee!"
                : lang === "el" ? "Ρώτα τον Andreas, reee!"
                : lang === "fr" ? "Demande à Andreas, reee !"
                : "Ask Andreas anything, reee!"}
            </span>
            {/* Speech-bubble tail pointing to the round button */}
            <span
              aria-hidden
              className="absolute -right-1 bottom-2 sm:bottom-3 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-foreground rotate-45"
            />
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="andreas-chat-open"
            className="h-11 w-11 sm:h-14 sm:w-14 rounded-full bg-foreground text-background flex items-center justify-center shadow-lg hover:scale-105 hover:bg-foreground/90 transition-transform relative shrink-0"
            aria-label="Open Andreas chat"
          >
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            <span aria-hidden className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-accent border-2 border-background animate-pulse" />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-5 sm:right-5 z-50 sm:w-[380px] sm:max-h-[70vh] flex flex-col bg-background border border-border shadow-2xl sm:rounded-2xl overflow-hidden"
          role="dialog"
          aria-label="Andreas — wedding chatbot"
          data-testid="andreas-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-border bg-foreground text-background">
            <div className="w-9 h-9 rounded-full bg-accent/30 flex items-center justify-center shrink-0">
              <span className="font-serif-display text-lg">A</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-serif-display text-lg leading-none">{h.title}</p>
              <p className="text-[10px] tracking-[0.14em] uppercase text-background/60 mt-1 truncate">
                {h.subtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-background/70 hover:text-background hover:bg-background/10 transition-colors"
              aria-label="Close"
              data-testid="andreas-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[54vh] sm:max-h-[52vh] bg-[hsl(32_18%_96%)]"
            data-testid="andreas-chat-messages"
          >
            {messages.length === 0 && (
              <p className="text-sm text-foreground/60 font-serif-display italic leading-relaxed">
                {lang === "pl" ? "Yasas! Andreas tu. Zapytaj mnie o wesele albo o Cypr." :
                 lang === "el" ? "Γεια! Ο Andreas εδώ. Ρωτήστε με για τον γάμο ή την Κύπρο." :
                 lang === "fr" ? "Yasas ! Andreas ici. Demandez-moi tout sur le mariage ou Chypre." :
                 "Yasas! Andreas here. Ask me anything about the wedding or Cyprus."}
              </p>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                data-testid={`andreas-msg-${i}`}
              >
                <div
                  className={
                    "max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed " +
                    (m.role === "user"
                      ? "bg-foreground text-background rounded-br-sm"
                      : m.error
                      ? "bg-destructive/10 text-destructive rounded-bl-sm border border-destructive/20"
                      : "bg-background text-foreground rounded-bl-sm border border-border")
                  }
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="andreas-typing">
                <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-background border border-border inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-foreground/40" />
                  <span className="text-xs text-foreground/50 italic">Andreas is thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Starter chips */}
          {messages.length === 0 && (
            <div className="px-4 py-2 border-t border-border flex flex-wrap gap-1.5 bg-background" data-testid="andreas-starters">
              {starters.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-border hover:border-foreground/40 text-foreground/70 hover:text-foreground transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={onSubmit} className="p-3 border-t border-border bg-background flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={h.placeholder}
              maxLength={500}
              disabled={busy}
              data-testid="andreas-input"
              className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-foreground/40"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="andreas-send"
              className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AndreasChat;
